import { useState } from 'react';
import {
  Search,
  Select,
  SelectItem,
  Tag,
  InlineLoading,
  InlineNotification,
  Button,
} from '@carbon/react';
import { Filter, Reset } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { knowledgeSearchApi } from '@/lib/api/knowledge-search.api';
import {
  ENTITY_CANDIDATE_TYPES,
  ENTITY_TYPE_GROUPS,
} from '@snxt/snxt-onto-share';
import type { EntityHit } from '@snxt/snxt-onto-share';
import './FacetSearch.scss';

interface EntitiesFacetProps {
  kbId: string;
}

type CarbonTagType =
  | 'teal' | 'cyan' | 'blue' | 'purple' | 'magenta' | 'red'
  | 'green' | 'gray' | 'cool-gray' | 'warm-gray'
  | 'high-contrast' | 'outline';

/** Map each entity type to a Carbon Tag type via the group color. */
const GROUP_COLOR_MAP: Record<string, CarbonTagType> = {
  teal: 'teal',
  blue: 'blue',
  green: 'green',
  purple: 'purple',
  'warm-gray': 'warm-gray',
};

function typeColor(t: string): CarbonTagType {
  for (const group of Object.values(ENTITY_TYPE_GROUPS)) {
    if ((group.types as readonly string[]).includes(t)) {
      return GROUP_COLOR_MAP[group.color] ?? 'outline';
    }
  }
  return 'outline';
}

export function EntitiesFacet({ kbId }: EntitiesFacetProps) {
  const [q, setQ] = useState('');
  const [entityType, setEntityType] = useState('');
  const [params, setParams] = useState<{ q: string; entityType: string } | null>(null);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['knowledge-search-entities', kbId, params],
    queryFn: () =>
      knowledgeSearchApi.searchEntities({
        kbId,
        q: params?.q || undefined,
        entityType: params?.entityType || undefined,
        limit: 200,
      }),
    enabled: params !== null,
  });

  const handleSearch = () => {
    setParams({ q: q.trim(), entityType });
  };

  const handleReset = () => {
    setQ('');
    setEntityType('');
    setParams(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const hits: EntityHit[] = data?.hits ?? [];
  const byType: Record<string, number> = data?.by_type ?? {};

  // BACKLOG §7 follow-up: this KB's own vocabulary (whatever `entity_taxonomy.resolve_entity_taxonomy`
  // resolved for it — an SDLC ontology's labels included) is what `by_type` actually carries, once a
  // search has run. `ENTITY_CANDIDATE_TYPES` is only the pre-search suggestion, for the moment before
  // `data` exists: a `<Select>` frozen on the 16-type scientific list would silently be unable to filter
  // by a type like "Feature" that a search result already shows.
  const typeOptions: readonly string[] =
    Object.keys(byType).length > 0 ? Object.keys(byType).sort() : ENTITY_CANDIDATE_TYPES;

  return (
    <div className="facet-search__panel">
      {/* Filters bar */}
      <div className="facet-search__filters">
        <Search
          id="entities-search-q"
          labelText="Search entities"
          placeholder="Filter by entity name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKeyDown}
          size="md"
          className="facet-search__text-filter"
        />

        <Select
          id="entities-type-filter"
          labelText="Entity type"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="facet-search__type-select"
        >
          <SelectItem value="" text="All types" />
          {typeOptions.map((t) => (
            <SelectItem key={t} value={t} text={t} />
          ))}
        </Select>

        <Button
          kind="primary"
          size="md"
          renderIcon={Filter}
          onClick={handleSearch}
          disabled={isLoading || isFetching}
        >
          Search
        </Button>

        <Button
          kind="ghost"
          size="md"
          renderIcon={Reset}
          onClick={handleReset}
          hasIconOnly
          iconDescription="Reset filters"
        />
      </div>

      {/* Status */}
      {(isLoading || isFetching) && (
        <InlineLoading description="Searching entities…" />
      )}

      {isError && (
        <InlineNotification
          kind="error"
          title="Search failed"
          hideCloseButton
        />
      )}

      {params && !isLoading && !isFetching && !isError && hits.length === 0 && (
        <div className="facet-search__empty">No entities found for these filters.</div>
      )}

      {/* Type facet counts */}
      {Object.keys(byType).length > 0 && (
        <div className="facet-search__type-pills">
          {Object.entries(byType)
            .sort(([, a], [, b]) => b - a)
            .map(([t, count]) => (
              <button
                key={t}
                className={`facet-search__type-pill${entityType === t ? ' facet-search__type-pill--active' : ''}`}
                onClick={() => {
                  const next = entityType === t ? '' : t;
                  setEntityType(next);
                  setParams({ q: q.trim(), entityType: next });
                }}
              >
                <Tag size="sm" type={typeColor(t)}>
                  {t}
                </Tag>
                <span className="facet-search__type-count">{count}</span>
              </button>
            ))}
        </div>
      )}

      {/* Results */}
      {hits.length > 0 && (
        <>
          <div className="facet-search__count">
            {hits.length} entit{hits.length !== 1 ? 'ies' : 'y'}
          </div>

          <div className="facet-search__entity-list">
            {hits.map((h, i) => (
              <div
                key={`${h.extraction_id}-${h.entity_index}-${i}`}
                className="facet-search__entity-card"
              >
                <div className="facet-search__entity-header">
                  <Tag size="sm" type={typeColor(h.entity_type)}>
                    {h.entity_type}
                  </Tag>
                  <span className="facet-search__entity-name">{h.name}</span>
                  {h.value && (
                    <span className="facet-search__entity-metric">
                      {h.value}
                      {h.unit && <em> {h.unit}</em>}
                    </span>
                  )}
                </div>
                {h.context && (
                  <div className="facet-search__entity-context">
                    "{h.context}"
                  </div>
                )}
                <div className="facet-search__entity-source">
                  {h.knowledge_object_name ?? h.knowledge_object_id}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Made with Bob
